import { z } from 'zod';
/**
 * The contract's 15 template keys, and one more.
 *
 * `application_unfinished` is not in the Interface Contract: it reminds
 * somebody who made an account and never sent an application that the window
 * is closing. The contract has no template for anyone who is not already an
 * applicant, an adjudicator or a conductor, and this round found two thousand
 * such people. Recorded in docs/CONTRACT-NOTES.md, which is where every other
 * deviation lives, because adding a template key is an interface change.
 */
export const TemplateKey = z.enum([
    'application_received',
    'video_link_broken',
    'adjudicator_invite',
    'adjudicator_reminder',
    'adjudicator_thanks',
    'adjudicator_changes_requested',
    'ensemble_signed_off',
    'outcome_selected',
    'outcome_reserve',
    'outcome_unsuccessful',
    'reserve_offer',
    'principal_invitation',
    'deadline_extended',
    'offer_reminder',
    'section_ordering',
    'application_unfinished',
]);
export const Channel = z.enum(['email', 'sms']);
/**
 * The merge fields each template requires. A send missing one is a 422, so this
 * table is the specification rather than documentation.
 */
export const MERGE_FIELDS = {
    application_received: ['name', 'instrument'],
    video_link_broken: ['name', 'piece', 'fixBy'],
    adjudicator_invite: ['name', 'panels', 'inviteLink', 'deadline'],
    adjudicator_reminder: ['name', 'panels', 'deadline'],
    adjudicator_thanks: ['name', 'panels'],
    adjudicator_changes_requested: ['name', 'panel', 'comment', 'link'],
    ensemble_signed_off: ['audition'],
    /*
      * No part. Which chair somebody takes is not settled when the offer goes
      * out — one person declining moves everybody below them — and a part named
      * in an offer is a part somebody has been promised. It is decided later and
      * told to them later.
      */
    outcome_selected: ['name', 'section', 'role', 'deadline', 'link'],
    outcome_reserve: ['name', 'section'],
    outcome_unsuccessful: ['name', 'instrument'],
    reserve_offer: ['name', 'section', 'deadline', 'link'],
    principal_invitation: ['name', 'section', 'deadline', 'link'],
    deadline_extended: ['name', 'what', 'newDeadline'],
    offer_reminder: ['name', 'deadline', 'link'],
    section_ordering: ['name', 'section', 'ordering'],
    /*
     * None. The wording the team asked for names nobody and counts nothing --
     * it is the same letter to everyone, so there is nothing to merge and no
     * way for a merge field to be missing and hold up two thousand sends.
     */
    /*
     * None. The unsubscribe line is appended by notify rather than written into
     * the body: as a merge field it would have to be declared on every template
     * that wanted it, and a declared field with no value is a 422 -- so one
     * message addressed to a bare email rather than an account would fail to
     * send at all.
     */
    application_unfinished: [],
};
/** Which channels each template may be sent on. */
export const TEMPLATE_CHANNELS = {
    application_received: ['email'],
    video_link_broken: ['email', 'sms'],
    adjudicator_invite: ['email'],
    adjudicator_reminder: ['email'],
    adjudicator_thanks: ['email'],
    adjudicator_changes_requested: ['email'],
    ensemble_signed_off: ['email'],
    outcome_selected: ['email', 'sms'],
    outcome_reserve: ['email'],
    outcome_unsuccessful: ['email'],
    reserve_offer: ['email', 'sms'],
    principal_invitation: ['email', 'sms'],
    deadline_extended: ['email'],
    offer_reminder: ['email', 'sms'],
    section_ordering: ['email'],
    application_unfinished: ['email'],
};
/**
 * Templates whose data carries a ranking or ordering. The provisional-rankings
 * line is built into these templates, so a caller cannot forget it.
 */
export const CARRIES_PROVISIONAL_LINE = [
    'outcome_selected',
    'reserve_offer',
    'principal_invitation',
    'section_ordering',
];
export const PROVISIONAL_RANKINGS_LINE = 'These are provisional rankings. Suzie may change seating arrangements and ' +
    'section assignments in rehearsal.';
/**
 * Merge fields the notification service resolves from Logto, given the
 * recipient's subject. A caller may still pass one — for a name that should
 * differ from the one on the account — but it is never required of them.
 */
export const RESOLVED_BY_NOTIFY = new Set(['name']);
export const SendMessageRequest = z
    .object({
    templateKey: TemplateKey,
    channel: Channel,
    to: z
        .object({
        userId: z.string().optional(),
        email: z.string().email().optional(),
        phone: z.string().optional(),
    })
        .refine((v) => Boolean(v.userId) || Boolean(v.email), {
        message: 'Provide userId or email',
    }),
    data: z.record(z.string(), z.unknown()),
    applicationId: z.string().optional(),
    batchId: z.string().optional(),
})
    .superRefine((v, ctx) => {
    for (const field of MERGE_FIELDS[v.templateKey]) {
        // The fields the notification service fills in itself are not the
        // caller's to provide. Names live in Logto, and a caller that had to
        // pass one would be holding a copy of something it does not own.
        if (RESOLVED_BY_NOTIFY.has(field))
            continue;
        if (!(field in v.data)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Missing merge field: ${field}` });
        }
    }
    if (!TEMPLATE_CHANNELS[v.templateKey].includes(v.channel)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Template ${v.templateKey} cannot be sent on ${v.channel}`,
        });
    }
});
export const SendMessageResponse = z.object({
    messageId: z.string(),
    status: z.enum(['queued', 'sent', 'failed']),
});
/** Up to 500 per call, per the contract. */
export const BatchRequest = z.object({
    kind: z.string(),
    messages: z.array(SendMessageRequest).min(1).max(500),
});
export const BatchResponse = z.object({ batchId: z.string(), queued: z.number().int() });
export const MessageStatusResponse = z.object({
    status: z.enum(['queued', 'sent', 'failed']),
    sentAt: z.string().datetime().nullable(),
    deliveryStatus: z.string().nullable(),
});
export const TemplateListResponse = z.object({
    templates: z.array(z.object({
        key: TemplateKey,
        channel: Channel,
        approved: z.boolean(),
        mergeFields: z.array(z.string()),
    })),
});
//# sourceMappingURL=notify.js.map